import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { buildDocFilename } from "@/lib/filename";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Building2,
  FileText,
  ClipboardCheck,
  Download,
  Eye,
  LogOut,
  Phone,
  Fingerprint,
  Shield,
  Smartphone,
  Share2,
  Plus,
  MoreVertical,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "imaggio_client_portal_token";

export default function ClientPortal() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [client, setClient] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [pvs, setPvs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [telefon, setTelefon] = useState("");
  const [cui, setCui] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("contracts");
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [platform, setPlatform] = useState("other"); // "ios" | "android" | "other"
  const [isStandalone, setIsStandalone] = useState(false);

  // Swap the manifest to the client-portal one so "Install app" targets /client-portal.
  useEffect(() => {
    const existing = document.querySelector('link[rel="manifest"]');
    const prev = existing?.href;
    if (existing) existing.href = "/manifest-client.json";
    // Update theme color (gold accent for the portal)
    const theme = document.querySelector('meta[name="theme-color"]');
    const prevTheme = theme?.content;
    if (theme) theme.content = "#B08D57";
    // Listen for the install prompt
    const onBip = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", onBip);
    // Detect platform
    const ua = navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);
    setPlatform(isIos ? "ios" : isAndroid ? "android" : "other");
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    setIsStandalone(!!standalone);
    if (isIos && !standalone) setShowIosHint(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      if (existing && prev) existing.href = prev;
      if (theme && prevTheme) theme.content = prevTheme;
    };
  }, []);

  const installApp = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      setInstallPrompt(null);
      if (choice?.outcome === "accepted") {
        toast.success("Aplicația a fost instalată pe telefon.");
      }
      return;
    }
    // Nu avem eventul → deschide ghidul manual
    setShowInstallGuide(true);
  };

  const auth = { headers: token ? { Authorization: `Bearer ${token}` } : {} };

  const loadData = async (tk) => {
    setLoading(true);
    try {
      const [me, ct, pv] = await Promise.all([
        axios.get(`${API}/client-portal/me`, { headers: { Authorization: `Bearer ${tk}` } }),
        axios.get(`${API}/client-portal/contracts`, { headers: { Authorization: `Bearer ${tk}` } }),
        axios.get(`${API}/client-portal/pvs`, { headers: { Authorization: `Bearer ${tk}` } }),
      ]);
      setClient(me.data.client);
      setContracts(ct.data || []);
      setPvs(pv.data || []);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
      toast.error(e.response?.data?.detail || "Sesiune expirată. Reautentifică-te.");
    }
    setLoading(false);
  };

  useEffect(() => { if (token) loadData(token); }, [token]);

  const login = async (e) => {
    e.preventDefault();
    if (!telefon || !cui) { toast.error("Introdu telefonul și CUI-ul"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/client-portal/login`, { telefon, cui });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success(`Bun venit, ${data.client.denumire}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Autentificare eșuată");
    }
    setBusy(false);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setClient(null); setContracts([]); setPvs([]);
    setTelefon(""); setCui("");
  };

  const viewDoc = async (kind, id, numar) => {
    try {
      const url = `${API}/client-portal/${kind}/${id}/pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("PDF eroare");
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
    } catch { toast.error("Nu s-a putut deschide documentul"); }
  };

  const downloadDoc = async (kind, id, numar) => {
    try {
      const url = `${API}/client-portal/${kind}/${id}/pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("PDF eroare");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = buildDocFilename(kind, client, numar || id);
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch { toast.error("Descărcare eșuată"); }
  };

  if (!token || !client) {
    // ---- LOGIN VIEW ----
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-8 border border-gold/30">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold-soft mb-3">
              <Shield className="w-8 h-8 text-gold-deep" />
            </div>
            <h1 className="font-display font-black text-3xl text-gold-deep">Portal Client</h1>
            <p className="text-sm text-slate-500 mt-1">IMAGGIO 2020 TIM SRL</p>
            <p className="text-xs text-muted-foreground mt-2">
              Autentifică-te cu numărul de telefon și CUI-ul firmei tale ca să vezi contractele și PV-urile.
            </p>
            {installPrompt && (
              <Button
                type="button"
                onClick={installApp}
                className="mt-4 w-full btn-gold hover:btn-gold h-12 text-base"
                data-testid="client-install-app"
              >
                <Smartphone className="w-5 h-5 mr-2" /> Instalează aplicația pe telefon
              </Button>
            )}
            {!installPrompt && !isStandalone && (
              <Button
                type="button"
                onClick={() => setShowInstallGuide(true)}
                variant="outline"
                className="mt-4 w-full border-gold text-gold-deep hover:bg-gold-soft h-12 text-base"
                data-testid="client-install-guide-btn"
              >
                <Smartphone className="w-5 h-5 mr-2" /> Instalează pe telefon (ca aplicație)
              </Button>
            )}
            {isStandalone && (
              <p className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md py-2 px-3">
                ✓ Aplicația e deja instalată pe acest telefon
              </p>
            )}
            {showIosHint && (
              <div className="mt-4 text-[11px] bg-blue-50 border border-blue-200 rounded-md p-3 text-blue-900 text-left">
                <b>Instalează pe iPhone:</b> apasă butonul <b>Share</b> (⇪) din Safari →
                „Adaugă la ecranul de start" → confirmă. Aplicația va apărea ca icon separat pe telefon.
              </div>
            )}
          </div>

          {/* Ghid pas-cu-pas pentru instalare */}
          <Dialog open={showInstallGuide} onOpenChange={setShowInstallGuide}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl flex items-center gap-2">
                  <Smartphone className="w-6 h-6 text-gold-deep" />
                  Instalează pe telefon
                </DialogTitle>
                <DialogDescription>
                  Pași rapizi ca să deschizi portalul ca aplicație (fără magazin de aplicații).
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                {platform === "ios" ? (
                  <div className="space-y-3">
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">iPhone / iPad (Safari)</div>
                    <StepRow n={1} icon={<Share2 className="w-4 h-4" />}>
                      Apasă butonul <b>Share</b> din bara de jos în Safari.
                    </StepRow>
                    <StepRow n={2} icon={<Plus className="w-4 h-4" />}>
                      Alege <b>„Adaugă la ecranul de start"</b>.
                    </StepRow>
                    <StepRow n={3}>
                      Confirmă cu <b>„Adaugă"</b> — iconița IMAGGIO Client va apărea pe ecranul principal.
                    </StepRow>
                    <div className="text-[11px] italic text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                      Notă: iOS acceptă instalare doar din <b>Safari</b>. Dacă ești în Chrome, copiază linkul și deschide-l în Safari.
                    </div>
                  </div>
                ) : platform === "android" ? (
                  <div className="space-y-3">
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Android (Chrome / Edge / Samsung Internet)</div>
                    <StepRow n={1} icon={<MoreVertical className="w-4 h-4" />}>
                      Apasă meniul cu <b>3 puncte</b> (dreapta sus în browser).
                    </StepRow>
                    <StepRow n={2}>
                      Alege <b>„Instalare aplicație"</b> sau <b>„Adaugă pe ecranul de start"</b>.
                    </StepRow>
                    <StepRow n={3}>
                      Confirmă cu <b>„Instalare"</b> — iconița IMAGGIO Client va apărea pe telefon.
                    </StepRow>
                    <div className="text-[11px] italic text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
                      Sfat: după instalare, șterge scurtătura veche din browser și folosește direct iconița IMAGGIO.
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <StepRow n={1}>
                      Deschide acest link direct de pe telefonul tău (nu de pe calculator).
                    </StepRow>
                    <StepRow n={2}>
                      În browserul telefonului, apasă meniul (3 puncte pe Android, Share pe iPhone).
                    </StepRow>
                    <StepRow n={3}>
                      Alege <b>„Adaugă pe ecranul de start"</b> sau <b>„Instalare aplicație"</b>.
                    </StepRow>
                  </div>
                )}

                <div className="pt-3 border-t border-border">
                  <p className="text-xs text-slate-600 mb-1">Link către portal:</p>
                  <div className="flex gap-2">
                    <code className="flex-1 text-xs bg-slate-100 px-2 py-2 rounded truncate">{window.location.origin + "/client-portal"}</code>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard?.writeText(window.location.origin + "/client-portal");
                        toast.success("Link copiat");
                      }}
                    >
                      Copiază
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <form onSubmit={login} className="space-y-4">
            <div>
              <Label className="label-eyebrow flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" /> Telefon
              </Label>
              <Input
                type="tel"
                value={telefon}
                onChange={(e) => setTelefon(e.target.value)}
                placeholder="ex: 0721234567"
                className="mt-2 h-12 text-lg"
                autoFocus
                data-testid="client-login-phone"
              />
            </div>
            <div>
              <Label className="label-eyebrow flex items-center gap-1.5">
                <Fingerprint className="w-3.5 h-3.5" /> CUI firmă
              </Label>
              <Input
                type="text"
                value={cui}
                onChange={(e) => setCui(e.target.value.toUpperCase())}
                placeholder="ex: RO12345678"
                className="mt-2 h-12 text-lg font-mono"
                data-testid="client-login-cui"
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-12 btn-gold hover:btn-gold text-base" data-testid="client-login-submit">
              {busy ? "Se autentifică..." : "Autentifică-te"}
            </Button>
            <p className="text-[11px] text-center text-muted-foreground pt-2 border-t border-border">
              Datele nu se potrivesc? Contactează IMAGGIO 2020 TIM SRL pentru corectare telefon/CUI în sistemul lor.
            </p>
          </form>
        </div>
      </div>
    );
  }

  // ---- DASHBOARD VIEW ----
  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-portal-dashboard">
      <Toaster position="top-center" />
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white border-b-4 border-gold">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Building2 className="w-8 h-8 text-gold shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Portal Client · IMAGGIO 2020 TIM</p>
              <h1 className="font-display font-black text-lg md:text-xl truncate">{client.denumire}</h1>
              <p className="text-[11px] text-slate-400 font-mono">CUI: {client.cui} · Tel: {client.telefon}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={logout} className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="client-logout">
            <LogOut className="w-4 h-4 mr-2" /> Ieșire
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* KPI cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white border border-border rounded-md p-4 flex items-center gap-3">
            <FileText className="w-8 h-8 text-amber-600" />
            <div>
              <p className="text-3xl font-black font-display">{contracts.length}</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Contracte totale</p>
            </div>
          </div>
          <div className="bg-white border border-border rounded-md p-4 flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8 text-emerald-600" />
            <div>
              <p className="text-3xl font-black font-display">{pvs.length}</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Procese verbale</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setTab("contracts")}
            className={`px-4 py-2 font-semibold text-sm border-b-2 transition-colors ${tab === "contracts" ? "border-gold text-gold-deep" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            data-testid="tab-contracts"
          >
            Contracte ({contracts.length})
          </button>
          <button
            onClick={() => setTab("pvs")}
            className={`px-4 py-2 font-semibold text-sm border-b-2 transition-colors ${tab === "pvs" ? "border-gold text-gold-deep" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            data-testid="tab-pvs"
          >
            PV-uri ({pvs.length})
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-6">Se încarcă documentele...</p>
        ) : tab === "contracts" ? (
          contracts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8 bg-white rounded-md border">Nu ai încă niciun contract înregistrat.</p>
          ) : (
            <div className="space-y-3">
              {contracts.map((c) => (
                <DocRow key={c.id}
                  title={`Contract ${c.numar}`}
                  subtitle={c.locatie || c.obiect || "Servicii DDD"}
                  meta={[
                    new Date(c.data).toLocaleDateString("ro-RO"),
                    c.durata_luni ? `${c.durata_luni} luni` : null,
                    c.valoare ? `${Number(c.valoare).toLocaleString("ro-RO")} ${c.moneda || "RON"}` : null,
                  ].filter(Boolean).join(" · ")}
                  onView={() => viewDoc("contracts", c.id, c.numar)}
                  onDownload={() => downloadDoc("contracts", c.id, c.numar)}
                  testid={`client-contract-${c.id}`}
                />
              ))}
            </div>
          )
        ) : (
          pvs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8 bg-white rounded-md border">Nu ai încă niciun PV înregistrat.</p>
          ) : (
            <div className="space-y-3">
              {pvs.map((p) => (
                <DocRow key={p.id}
                  title={`PV ${p.numar}`}
                  subtitle={[(p.servicii || []).join(" + "), p.locatie].filter(Boolean).join(" — ") || "Intervenție DDD"}
                  meta={[
                    new Date(p.data).toLocaleDateString("ro-RO"),
                    p.contract_numar ? `Contract ${p.contract_numar}` : null,
                    p.auto_generated ? "Programat" : null,
                  ].filter(Boolean).join(" · ")}
                  onView={() => viewDoc("pvs", p.id, p.numar)}
                  onDownload={() => downloadDoc("pvs", p.id, p.numar)}
                  testid={`client-pv-${p.id}`}
                />
              ))}
            </div>
          )
        )}

        <footer className="pt-4 pb-8 text-center text-xs text-muted-foreground">
          IMAGGIO 2020 TIM SRL · Dezinfecție · Dezinsecție · Deratizare
        </footer>
      </main>
    </div>
  );
}

function DocRow({ title, subtitle, meta, onView, onDownload, testid }) {
  return (
    <div className="bg-white border border-border rounded-md p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:border-gold/50 transition-colors" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold text-base">{title}</p>
        <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
        <p className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-1">{meta}</p>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onView} className="border-gold text-gold-deep hover:bg-gold hover:text-white" data-testid={`${testid}-view`}>
          <Eye className="w-4 h-4 mr-2" /> Vezi
        </Button>
        <Button variant="outline" size="sm" onClick={onDownload} data-testid={`${testid}-download`}>
          <Download className="w-4 h-4 mr-2" /> Descarcă
        </Button>
      </div>
    </div>
  );
}

function StepRow({ n, icon, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="shrink-0 w-7 h-7 rounded-full bg-gold-soft text-gold-deep font-bold flex items-center justify-center text-xs">{n}</div>
      <div className="flex-1 pt-0.5">
        {icon && <span className="inline-flex items-center gap-1.5 mr-1 text-slate-700">{icon}</span>}
        <span>{children}</span>
      </div>
    </div>
  );
}
